import { queryClient } from "@/base/libs/react-query";
import { SortDescriptor } from "@/base/model/sort-descriptor";
import { feedbackApi } from "@/service/client";
import { useQuery } from "@tanstack/react-query";
import { atom, useAtom } from "jotai";

const sortDescriptor: SortDescriptor = {
  column: "created_at",
  direction: "descending",
};

const sortDescriptorAtom = atom<SortDescriptor>(sortDescriptor);

export const useFeedbackController = () => {
  const [sortDescriptor, setSortDescriptor] = useAtom(sortDescriptorAtom);

  const { data: feedbackList } = useQuery({
    queryKey: ["feedback", sortDescriptor],
    queryFn: () => feedbackApi.list(sortDescriptor),
  });

  const createFeedback = async (
    title: string,
    description: string,
    email: string
  ) => {
    await feedbackApi.create({ title, description, email });
    queryClient.invalidateQueries({ queryKey: ["feedback"] });
  };

  const deleteFeedback = async (id: string) => {
    await feedbackApi.delete(id);
    queryClient.invalidateQueries({ queryKey: ["feedback"] });
  };

  const updateFeedback = async (
    id: string,
    title: string,
    description: string
  ) => {
    await feedbackApi.update(id, { title, description });
    queryClient.invalidateQueries({ queryKey: ["feedback"] });
  };

  const onSortChange = (descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor);
    queryClient.invalidateQueries({ queryKey: ["feedback"] });
  };

  return {
    feedbackList,
    createFeedback,
    deleteFeedback,
    updateFeedback,
    sortDescriptor,
    onSortChange,
  };
};

export const useFeedbackIdController = (id: string) => {
  const { data: feedback } = useQuery({
    queryKey: ["feedback", id],
    queryFn: () => feedbackApi.get(id),
  });

  const createComment = async (id: string, content: string, email: string) => {
    await feedbackApi.createComment(id, { content, email });
    queryClient.invalidateQueries({ queryKey: ["feedback", id] });
  };

  return { feedback, createComment };
};
